import React from "react";
import FunctionsIcon from "@mui/icons-material/Functions";
import LanguageIcon from "@mui/icons-material/Language";
import TranslateIcon from "@mui/icons-material/Translate";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import BiotechRoundedIcon from "@mui/icons-material/BiotechRounded";
import YardRoundedIcon from "@mui/icons-material/YardRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import { SubjectColor, PaperColor } from "styles/theme/themes";
import { SxProps } from "@mui/material/styles";
import { ASSIGNMENT_SUBMISSION_STATES } from "./constants";

export function openSidebar() {
  if (typeof window !== "undefined") {
    document.body.style.overflow = "hidden";
    document.documentElement.style.setProperty("--SideNavigation-slideIn", "1");
  }
}

export function closeSidebar() {
  if (typeof window !== "undefined") {
    document.documentElement.style.removeProperty("--SideNavigation-slideIn");
    document.body.style.removeProperty("overflow");
  }
}

export function toggleSidebar() {
  if (typeof window !== "undefined" && typeof document !== "undefined") {
    const slideIn = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue("--SideNavigation-slideIn");

    // Add a small delay to ensure animations complete properly
    if (slideIn) {
      closeSidebar();
    } else {
      openSidebar();
    }

    // Dispatch event to notify components that sidebar state has changed
    const event = new CustomEvent("sidebarToggle", {
      detail: { isOpen: !slideIn },
    });
    document.dispatchEvent(event);
  }
}

export function isStringNullOrEmpty(value) {
  return value === undefined || value === null || value === "";
}

export function isArray(value) {
  return (value !== undefined || value !== null) && Array.isArray(value);
}
export function isArrayAndNotEmpty(value) {
  return isArray(value) && value.length > 0;
}

export function compareListsById(list1, list2) {
  const idsList1 = list1.map((item) => item.id);
  const idsList2 = list2.map((item) => item.id);

  return (
    new Set(idsList1).size === new Set(idsList2).size &&
    idsList1.every((id) => idsList2.includes(id))
  );
}

export function compareListsByContent(list1, list2) {
  if (list1 === undefined || list2 === undefined) {
    return false;
  }
  return (
    new Set(list1).size === new Set(list2).size &&
    list1.every((id) => list2.includes(id))
  );
}

export function numberListBetween(start = 0, end = 10) {
  let list: number[] = [];
  for (var i = start; i < end; i++) {
    list.push(i);
  }

  return list;
}

export const deepCopy = (value) => JSON.parse(JSON.stringify(value));

// add an optional property of size in number with default value 24
export function getSubjectIcon(
  subject?: string,
  size = 24
): React.ReactElement {
  const defaultSx = { fontSize: size, color: "primary.500" };

  if (!subject) return <FunctionsIcon sx={defaultSx} />;

  switch (subject.toLowerCase()) {
    case "math":
    case "mathematics":
      return (
        <FunctionsIcon
          sx={{ fontSize: size, color: SubjectColor.mathematics[500] }}
        />
      );
    case "english":
      return (
        <LanguageIcon
          sx={{ fontSize: size, color: SubjectColor.english[500] }}
        />
      );
    case "chinese":
      return (
        <TranslateIcon
          sx={{ fontSize: size, color: SubjectColor.chinese[500] }}
        />
      );
    case "physics":
      return (
        <YardRoundedIcon
          sx={{ fontSize: size, color: SubjectColor.physics[500] }}
        />
      );
    case "chemistry":
      return (
        <ScienceRoundedIcon
          sx={{ fontSize: size, color: SubjectColor.chemistry[500] }}
        />
      );
    case "biology":
      return (
        <BiotechRoundedIcon
          sx={{ fontSize: size, color: SubjectColor.biology[500] }}
        />
      );
    case "economics":
      return (
        <ShowChartRoundedIcon
          sx={{ fontSize: size, color: SubjectColor.economics[500] }}
        />
      );
    default:
      return <FunctionsIcon sx={{ fontSize: size, color: "primary.500" }} />;
  }
}

export function getSubjectIllustration(subject?: string) {
  try {
    switch (subject && subject.toLowerCase()) {
      case "math":
      case "mathematics":
        {
          return require("assets/illustration-math.svg")?.default;
        }
        break;
      case "chinese":
        {
          return require("assets/illustration-internet.svg")?.default;
        }
        break;
      case "english":
        {
          return require("assets/illustration-book-lover.svg")?.default;
        }
        break;
    }

    return require("assets/illustration-education.svg")?.default;
  } catch (err) {
    return require("assets/illustration-education.svg")?.default;
  }
}

export function getSubjectColor(subject?: string) {
  if (!subject) return SubjectColor.mathematics;
  switch (subject.toLowerCase()) {
    case "math":
    case "mathematics":
      return SubjectColor.mathematics;
    case "english":
      return SubjectColor.english;
    case "chinese":
      return SubjectColor.chinese;
    case "physics":
      return SubjectColor.physics;
    case "chemistry":
      return SubjectColor.chemistry;
    case "biology":
      return SubjectColor.biology;
    case "economics":
      return SubjectColor.economics;
    default:
      return SubjectColor.mathematics;
  }
}

export function getPaperColor(paper: string) {
  if (!paper) return PaperColor.p1;
  switch (paper.toLowerCase()) {
    case "p1":
      return PaperColor.p1;
    case "p2":
      return PaperColor.p2;
    default:
      return PaperColor.p1;
  }
}

export function getPapers(subject: string) {
  switch (subject) {
    case "Chinese":
    case "English":
      return ["P1", "P2"];
    case "Mathematics":
    case "Physics":
    case "Chemistry":
    case "Biology":
    case "Economics":
      return ["P1"];
    default:
      return [];
  }
}

export function getStatusDisplay(status: string | undefined): string {
  switch (status?.toLowerCase()) {
    case ASSIGNMENT_SUBMISSION_STATES.SUBMITTED.toLowerCase():
      return "Submitted";
    case ASSIGNMENT_SUBMISSION_STATES.GRADED.toLowerCase():
      return "Graded";
    case ASSIGNMENT_SUBMISSION_STATES.AUTO_GRADING.toLowerCase():
      return "Auto Grading";
    case ASSIGNMENT_SUBMISSION_STATES.AUTO_GRADED.toLowerCase():
      return "Auto Graded";
    case ASSIGNMENT_SUBMISSION_STATES.AUTO_GRADED_WARNING.toLowerCase():
      return "Auto Graded (Warning)";
    case ASSIGNMENT_SUBMISSION_STATES.LATE.toLowerCase():
      return "Late";
    case ASSIGNMENT_SUBMISSION_STATES.RETURNED.toLowerCase():
      return "Returned";
    case ASSIGNMENT_SUBMISSION_STATES.NOT_SUBMITTED.toLowerCase():
      return "Not Submitted";
    default:
      return "Not Submitted";
  }
}

export function validateYouTubeUrl(url) {
  if (url != undefined || url != "") {
    var regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|\?v=)([^#\&\?]*).*/;
    var match = url.match(regExp);
    if (match && match[2].length == 11) {
      return true;
    } else {
      return false;
    }
  }
}

export function validatePassword(password: string): boolean {
  const minLength = 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /[0-9]/.test(password);
 
  return (
    password.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers
  );
}

export function filterDataByKeyword({data, keyword, property = 'name'}: {data: Object[], keyword: string, property?: string}){
	try{

		return data.filter(item=> keyword ? item[property].toLowerCase().includes(keyword.toLowerCase()) : true)

	}
	catch(err){
		
		return data

	}
}

export const getYouTubeEmbedUrl = (url: string): string => {
  const match = url.match(/(?:https?:\/\/)?(?:www\.)?youtube\.com\/(?:watch\?v=|live\/)([^&?]+)|youtu\.be\/([^?&]+)/);
  const videoId = match ? match[1] || match[2] : null;
  return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
};

export function capitalizeFirstLetter(val) {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}